import React from 'react';

import http from 'api/http';
import { boatAPI, trailerAPI } from 'api/requests';

import Row from './Row';
import Pagination from 'Components/Pagination';
import RowsSelector from 'Components/RowsSelector';
import FiltersSection from './FiltersSection';
import TableHeader from './TableHeader';
import EmptyList from 'Components/EmptyList';
import moment from 'moment';
import { Input, inputStyleClasses } from 'Components/Input';
import { ROLES } from 'constants/roles';

const SEARCHABLE_FIELDS = ['userName', 'location'];

export default class List extends React.Component {
  defaultState = {
    activePage: 1,
    selectedRowsCount: 10,
    sortedBy: 'inspectionDate',
    sortAsc: -1,
    filter: {},
    searchString: '',
    year: '',
  };

  constructor(props) {
    super(props);

    const storedState = props.user.getStoredState('inspectionListState', this.defaultState);
    props.user.updateURLSearchString(storedState);

    this.state = {
      ...this.defaultState,
      ...storedState,
    };
  }

  componentDidMount() {
    const { user } = this.props;

    if (!user.getSelectedYear()) {
      this.getCurrentYear().then(currentYear => {
        this.handleSelectFilter('year')(currentYear);
      });
    } else {
      this.handleSelectFilter('year')(user.getSelectedYear());
    }
  }

  getCurrentYear = () => {
    const { user } = this.props;

    return new Promise((resolve, reject) => {
      switch (user.getActiveRole()) {
        case ROLES.BOAT_MANUFACTURER.serverName:
          http.get(boatAPI.currentModelYear).then(({ data }) => {
            resolve(data[1] || data[0]);
          });
          break;
        case ROLES.TRAILER_MANUFACTURER.serverName:
          http.get(trailerAPI.currentModelYear).then(({ data }) => {
            resolve(data);
          });
          break;
        default: {
          const selectedYear = new Date().getFullYear();
          resolve(selectedYear);
          break;
        }
      }
    });
  };

  componentWillUnmount() {
    const { user } = this.props;

    user.saveState('inspectionListState', this.state, ['inspectionList', 'listRequestInProcess', 'year']);
  }

  handleRowsSelect = (rowsCountSelected, totalRowsCount) => {
    const { user } = this.props;

    const selectedRowsCount = rowsCountSelected && rowsCountSelected < totalRowsCount ? rowsCountSelected : 0;

    const state = { selectedRowsCount, activePage: 1 };
    this.setState(state, user.updateURLSearchString(state));
  };

  _handleActivePageChange = activePage => {
    const { user } = this.props;

    this.setState({ activePage }, () => {
      user.updateURLSearchString({ activePage });
    });
  };

  _sortList = (a, b) => {
    const { sortedBy, sortAsc } = this.state;

    switch (sortedBy) {
      case 'inspectionDate':
        return moment(a.inspectionDate).isAfter(b.inspectionDate) ? sortAsc : -sortAsc;
      case 'year':
        return +a.year > +b.year ? sortAsc : -sortAsc;
      case 'user': {
        const userA = `${a.user.firstName} ${a.user.lastName}`;
        const userB = `${b.user.firstName} ${b.user.lastName}`;
        return userA > userB ? sortAsc : -sortAsc;
      }
      default:
        return (a[sortedBy] || ' ').toString().toLowerCase().replace(/\s+/g, ' ') >
          (b[sortedBy] || ' ').toString().toLowerCase().replace(/\s+/g, ' ')
          ? sortAsc
          : -sortAsc;
    }
  };

  handleSort = sortedBy => () => {
    const { user } = this.props;

    this.setState(
      prevState => ({ sortedBy, sortAsc: -prevState.sortAsc, activePage: 1 }),
      () => {
        user.updateURLSearchString({ sortedBy: this.state.sortedBy, sortAsc: this.state.sortAsc, activePage: 1 });
      }
    );
  };

  _filterList = inspectionList => {
    const { filter, searchString } = this.state;
    const { getReadableStatus } = this.props;

    const doesSearchableFieldsContainSearchString = element => {
      for (let searchableFieldIndex in SEARCHABLE_FIELDS) {
        switch (SEARCHABLE_FIELDS[searchableFieldIndex]) {
          case 'userName': {
            const userName = element.user ? `${element.user.firstName} ${element.user.lastName}` : '';
            if (userName.toLowerCase().includes(searchString.toLowerCase())) {
              return true;
            } else {
              break;
            }
          }
          default:
            if (element[SEARCHABLE_FIELDS[searchableFieldIndex]]) {
              return element[SEARCHABLE_FIELDS[searchableFieldIndex]]
                .toString()
                .toLowerCase()
                .includes(searchString.toLowerCase());
            }
        }
      }
      return false;
    };

    return inspectionList.filter(inspection => {
      for (let filterFieldName in filter) {
        switch (filterFieldName) {
          case 'year':
            if (+inspection.year !== +filter.year) {
              return false;
            }
            break;
          case 'status':
            if (getReadableStatus(inspection, true) !== filter.status) {
              return false;
            }
            break;
          default:
            return false;
        }
      }
      return !searchString || doesSearchableFieldsContainSearchString(inspection);
    });
  };

  handleSearchStringInput = value => {
    const { user } = this.props;
    this.setState({ searchString: value, activePage: 1 }, () => {
      user.updateURLSearchString({ searchString: value });
    });
  };

  handleSelectFilter = filterFieldName => filterElement => {
    const { user } = this.props;
    const newFilterFieldValue = filterElement.value !== undefined ? filterElement.value : filterElement;
    let newFilterState = this.state.filter;

    if (newFilterFieldValue === 'All') delete newFilterState[filterFieldName];
    else newFilterState[filterFieldName] = newFilterFieldValue;

    if (filterFieldName === 'year') {
      if (filterElement === 'All') {
        user.saveSelectedYear('');
      } else {
        user.saveSelectedYear(filterElement);
      }
    }

    this.setState({ filter: newFilterState, activePage: 1 }, () => {
      user.updateURLSearchString({ filter: this.state.filter, activePage: 1 });
    });
  };

  _getList = () => {
    const { activePage, selectedRowsCount, sortedBy } = this.state;
    const { user, inspectionList, createActionList, listRequestInProcess, performAction, getReadableStatus } =
      this.props;
    const filteredList = this._filterList(inspectionList).sort(this._sortList);
    const sliceFrom = (activePage - 1) * selectedRowsCount;
    const sliceTo = +selectedRowsCount ? activePage * selectedRowsCount : filteredList.length;

    let resultList = [];
    let resultLength = 0;

    if (!inspectionList.length) {
      if (listRequestInProcess) {
        resultList = [
          <div className='ect-spinner _height_120' key='0'>
            <div className='ect-spinner__image' style={{ width: 50, height: 50 }} />
          </div>,
        ];
      } else {
        resultList = [<EmptyList key='0'>No Inspections for selected manufacturer.</EmptyList>];
      }
    } else if (!filteredList.length) {
      resultList = [<EmptyList key='0'>No Inspections found with selected filters.</EmptyList>];
    } else {
      resultList = filteredList.slice(sliceFrom, sliceTo).map((inspection, index) => {
        return (
          <Row
            key={inspection.id}
            user={user}
            sortedBy={sortedBy}
            inspection={inspection}
            isOdd={!!(index % 2)}
            actionList={createActionList(inspection)}
            performAction={performAction}
            getReadableStatus={getReadableStatus}
            updateListEntryAfterStatusChange={this.updateListEntryAfterStatusChange}
          />
        );
      });
      resultLength = filteredList.length;
    }

    return {
      resultList,
      resultLength,
    };
  };

  render() {
    const { activePage, selectedRowsCount, filter, sortedBy, sortAsc, searchString } = this.state;
    const { currentModelYear, statuses } = this.props;
    const { resultList, resultLength } = this._getList();

    return (
      <section className='scene__scene-wrapper'>
        <section className='scene__content-wrapper list-scene__content-wrapper'>
          <header className='scene__scene-header list-scene__scene-header'>
            Inspection list
            <Input
              mainWrapperClassName='list-scene__header-search-field-wrapper'
              styleClass={inputStyleClasses.searchBar}
              placeholder='Type here to search'
              maxLength={250}
              value={searchString}
              onChange={this.handleSearchStringInput}
            />
          </header>

          <FiltersSection
            outCount={resultLength}
            handleRowsSelect={this.handleRowsSelect}
            rowsCount={selectedRowsCount}
            handleSelectFilter={this.handleSelectFilter}
            filterState={filter}
            statuses={statuses}
            currentModelYear={currentModelYear}
          />

          <TableHeader handleSortClick={this.handleSort} order={sortAsc === 1} sortedBy={sortedBy} />

          {resultList}

          <footer className='list-scene__footer'>
            <RowsSelector
              allRowsCount={resultLength}
              selectedRowsCount={selectedRowsCount}
              handleRowsSelect={this.handleRowsSelect}
            />
            <Pagination
              className='_height_80'
              activePage={activePage}
              handleClick={this._handleActivePageChange}
              allRowsCount={resultLength}
              selectedRowsCount={selectedRowsCount}
            />
          </footer>
        </section>
      </section>
    );
  }
}



// WEBPACK FOOTER //
// ./src/app/Scenes/Inspection/List/index.jsx